import { jahreseinkommenAnzeige } from "./steuerUndAusweisAngaben";
import type { MapperErgebnis, UebergangenerWert } from "./visitenkarteZuFormState";

/**
 * Übersetzt einen Gehaltsabrechnungs-Befund in die Feldschlüssel der
 * Personen-Anlage (`NewPrivateContactForm`).
 *
 * Zwei Festlegungen prägen diesen Mapper:
 *
 *  1. **Steuerklasse und Jahreseinkommen sind GESCHÜTZTE Angaben.** Der Server
 *     nimmt sie nur von den Kontaktverantwortlichen und der Leitung entgegen
 *     (`contact.geschuetzteFelder.ts`) und wirft sie sonst still weg. Ohne
 *     Erlaubnis werden sie deshalb erst gar nicht angeboten, sondern mit
 *     Begründung als übergangen ausgewiesen.
 *
 *  2. **Nur ein echter Jahreswert füllt das Jahreseinkommen.** Das Feld heißt
 *     „Jahresbruttoeinkommen"; eine Monatsabrechnung nennt aber das
 *     Monatsbrutto. Zwölf mal den Monat zu rechnen wäre bequem und wäre falsch,
 *     sobald Sonderzahlungen, Teiljahre oder wechselnde Bezüge im Spiel sind —
 *     und mit diesem Feld wird später bAV- und BU-Bedarf gerechnet. Übernommen
 *     wird deshalb ausschließlich ein auf dem Beleg ausgewiesener
 *     Kumulativwert. Das Monatsbrutto wird gelesen und angezeigt, aber als
 *     „kein Feld" gemeldet (ein Monatsfeld ist vorgesehen, existiert heute
 *     aber nicht).
 */

export type GehaltsabrechnungBefund = Partial<{
  titel: string;
  vorname: string;
  nachname: string;
  geburtsdatum: string;
  strasse: string;
  plz: string;
  ort: string;
  personalnummer: string;
  taetigkeit: string;
  steuerklasse: string;
  steuerId: string;
  sozialversicherungsnummer: string;
  kinderfreibetraege: string;
  konfession: string;
  krankenkasse: string;
  arbeitgeber: string;
  arbeitgeberStrasse: string;
  arbeitgeberPlz: string;
  arbeitgeberOrt: string;
  eintrittsdatum: string;
  abrechnungsmonat: string;
}> & {
  gesamtbruttoMonat?: number;
  steuerbruttoMonat?: number;
  svBruttoMonat?: number;
  nettoAuszahlung?: number;
  gesamtbruttoJahr?: number;
  steuerbruttoJahr?: number;
  /** Widersprüche aus der Betragsprüfung im Backend. */
  hinweise?: string[];
};

export type GehaltKontext = {
  /** Titel-Auswahl der Maske — heute nur Dr. / Prof. / Prof. Dr. */
  titelOptionen?: string[];
};

const gesetzt = (wert: string | undefined): wert is string => !!wert && wert.trim() !== "";

/** Gehaltsabrechnung → Personen-Maske. */
export const gehaltZuPrivatFormState = (
  befund: GehaltsabrechnungBefund,
  kontext: GehaltKontext = {},
): MapperErgebnis => {
  const werte: Record<string, string> = {};
  const uebergangen: UebergangenerWert[] = [];

  if (gesetzt(befund.titel)) {
    // Wie bei der Visitenkarte: Die Maske kennt nur eine kurze Auswahl, und
    // der Server verwirft einen Nichttreffer kommentarlos.
    const treffer = kontext.titelOptionen?.find(
      (o) => o.trim().toLowerCase() === befund.titel!.trim().toLowerCase(),
    );
    if (treffer) werte.title = treffer;
    else
      uebergangen.push({
        feld: "Titel",
        wert: befund.titel,
        grund: "steht nicht in der Titel-Auswahl der Maske",
      });
  }
  if (gesetzt(befund.vorname)) werte.firstname = befund.vorname;
  if (gesetzt(befund.nachname)) werte.lastname = befund.nachname;
  if (gesetzt(befund.geburtsdatum)) werte.dateofbirth = befund.geburtsdatum;
  if (gesetzt(befund.strasse)) werte.street = befund.strasse;
  if (gesetzt(befund.plz)) werte.postalcode = befund.plz;
  if (gesetzt(befund.ort)) werte.city = befund.ort;
  // Die Berufsbezeichnung der Abrechnung landet im selben Feld wie die
  // Position einer Visitenkarte — die Maske hat dafür nur „position".
  if (gesetzt(befund.taetigkeit)) werte.position = befund.taetigkeit;

  // ── Steuer- und Sozialangaben ────────────────────────────────────────────
  // Standen bis 09/2026 unter einem Sonderrecht: Der Server nahm sie nur von
  // den Kontaktverantwortlichen und der Leitung an, deshalb meldete der Mapper
  // sie anderen als übergangen, statt sie anzubieten und beim Speichern zu
  // verlieren. Die Sonderstellung ist entfallen — sie werden immer übernommen.
  if (gesetzt(befund.steuerklasse)) werte.taxClass = befund.steuerklasse;

  if (befund.gesamtbruttoJahr !== undefined) {
    // Das Feld erwartet die deutsche Schreibweise ("84.000"); der Payload-Bau
    // liest sie mit `jahreseinkommenAusEingabe` wieder ein.
    werte.annualIncome = jahreseinkommenAnzeige(befund.gesamtbruttoJahr);
  }

  // Die Sozialversicherungsnummer hat seit dem 14.09.2026 ein Zielfeld.
  // Übernommen wird sie SO, wie sie auf dem Beleg steht (mit Leerzeichen):
  // Sie wird verglichen, nicht gerechnet.
  if (gesetzt(befund.sozialversicherungsnummer)) {
    werte.socialSecurityNumber = befund.sozialversicherungsnummer;
  }


  // ── Angaben ohne Zielfeld ───────────────────────────────────────────────
  // Bewusst aufgeführt statt verschwiegen: Der Anwender soll sehen, was auf
  // dem Beleg stand, auch wenn KMVS es heute nicht speichert.
  const betragOhneZiel: Array<[string, number | undefined, string]> = [
    ["Monatsbrutto", befund.gesamtbruttoMonat, "kein Monatsfeld in der Kontaktmaske"],
    ["Steuerbrutto (Monat)", befund.steuerbruttoMonat, "kein Feld in der Kontaktmaske"],
    ["SV-Brutto (Monat)", befund.svBruttoMonat, "kein Feld in der Kontaktmaske"],
    ["Auszahlungsbetrag", befund.nettoAuszahlung, "kein Feld in der Kontaktmaske"],
    ["Steuerbrutto (Jahr)", befund.steuerbruttoJahr, "das Einkommensfeld nimmt das Gesamtbrutto"],
  ];
  for (const [label, wert, grund] of betragOhneZiel) {
    if (wert === undefined) continue;
    uebergangen.push({ feld: label, wert: jahreseinkommenAnzeige(wert) + " €", grund });
  }

  const textOhneZiel: Array<[string, string | undefined]> = [
    ["Steuer-ID", befund.steuerId],
    ["Krankenkasse", befund.krankenkasse],
    ["Personalnummer", befund.personalnummer],
    ["Kinderfreibeträge", befund.kinderfreibetraege],
    ["Konfession", befund.konfession],
    ["Eintrittsdatum", befund.eintrittsdatum],
  ];
  for (const [label, wert] of textOhneZiel) {
    if (gesetzt(wert)) {
      uebergangen.push({ feld: label, wert, grund: "kein Feld in der Kontaktmaske" });
    }
  }

  return { werte, uebergangen };
};

/** Anzeigenamen der Befundfelder für die Prüfliste. */
export const GEHALT_LABELS: Record<string, string> = {
  titel: "Titel",
  vorname: "Vorname",
  nachname: "Nachname",
  geburtsdatum: "Geburtsdatum",
  strasse: "Straße",
  plz: "PLZ",
  ort: "Ort",
  taetigkeit: "Tätigkeit",
  steuerklasse: "Steuerklasse",
  gesamtbruttoJahr: "Jahresbrutto (aufgelaufen)",
  gesamtbruttoMonat: "Monatsbrutto",
  steuerbruttoMonat: "Steuerbrutto (Monat)",
  svBruttoMonat: "SV-Brutto (Monat)",
  nettoAuszahlung: "Auszahlungsbetrag",
  steuerbruttoJahr: "Steuerbrutto (Jahr)",
  steuerId: "Steuer-ID",
  sozialversicherungsnummer: "SV-Nummer",
  krankenkasse: "Krankenkasse",
  personalnummer: "Personalnummer",
  kinderfreibetraege: "Kinderfreibeträge",
  konfession: "Konfession",
  eintrittsdatum: "Eintrittsdatum",
  arbeitgeber: "Arbeitgeber",
  abrechnungsmonat: "Abrechnungsmonat",
};

/** Reihenfolge der Prüfliste: erst die Person, dann Steuer, dann die Beträge. */
export const GEHALT_REIHENFOLGE: string[] = [
  "titel",
  "vorname",
  "nachname",
  "geburtsdatum",
  "strasse",
  "plz",
  "ort",
  "taetigkeit",
  "steuerklasse",
  "gesamtbruttoJahr",
  "gesamtbruttoMonat",
  "steuerbruttoMonat",
  "svBruttoMonat",
  "nettoAuszahlung",
  "steuerbruttoJahr",
  "steuerId",
  "sozialversicherungsnummer",
  "krankenkasse",
  "personalnummer",
  "kinderfreibetraege",
  "konfession",
  "eintrittsdatum",
];

/**
 * Beträge kommen als Zahl, alles andere als Text — die Prüfliste zeigt aber
 * überall Text an. Formatiert wird nach derselben Regel wie im Eingabefeld.
 */
export const gehaltAnzeigewert = (
  befund: GehaltsabrechnungBefund,
  feld: string,
): string => {
  const wert = (befund as Record<string, unknown>)[feld];
  if (wert === undefined || wert === null || wert === "") return "";
  if (typeof wert === "number") return jahreseinkommenAnzeige(wert) + " €";
  return String(wert);
};
